import { useEffect } from "react"
import { Head } from "@inertiajs/react"
import Lenis from "lenis"
import Navbar from "@/components/chalky/Navbar"
import Hero from "@/components/chalky/Hero"
import Services from "@/components/chalky/Services"
import Products from "@/components/chalky/Products"
import Clients from "@/components/chalky/Clients"
import Stats from "@/components/chalky/Stats"
import Contact from "@/components/chalky/Contact"
import Footer from "@/components/chalky/Footer"

interface ServiceItem {
  key: string
  title: string
  description: string
}

interface Product {
  key: string
  name: string
  baseline: string
  description: string
  features: string[]
  url: string
}

interface HomeProps {
  hero: {
    title_line1: string
    title_line2: string
    subtitle: string
    cta_discover: string
    cta_start_project: string
  }
  services: {
    section_title: string
    section_title_highlight: string
    items: ServiceItem[]
  }
  products: Product[]
  stats: { years: number }
  clients: {
    section_title: string
    section_title_highlight: string
  }
  contact: {
    section_title: string
    section_title_highlight: string
    labels: {
      full_name: string
      email: string
      message: string
    }
  }
  meta: {
    title: string
    description: string
  }
}

export default function Home({
  hero,
  services,
  products,
  stats,
  clients,
  contact,
  meta,
}: HomeProps) {
  // Initialize Lenis smooth scroll
  useEffect(() => {
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches

    if (prefersReducedMotion) return

    const lenis = new Lenis({
      duration: 1.2,
      easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    })

    const raf = (time: number) => {
      lenis.raf(time)
      requestAnimationFrame(raf)
    }

    requestAnimationFrame(raf)

    return () => {
      lenis.destroy()
    }
  }, [])

  return (
    <>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [{
            "@type": "ListItem",
            "position": 1,
            "name": "Accueil",
            "item": "https://chalky.fr"
          }]
        })}</script>
      </Head>

      <div className="bg-[#0a0a0f] min-h-screen">
        <Navbar />
        <Hero {...hero} />
        <Services {...services} />
        <Products products={products} />
        <Clients {...clients} />
        <Stats years={stats.years} />
        <Contact {...contact} />
        <Footer />
      </div>
    </>
  )
}
